import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectDocuments } from '@stripe/react-connect-js';
import { InformationalBanner, Spinner, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createAccountSession } from '../../../api/firebase/organizations';
import { logError } from '../../../rollbar';

// Public, browser-safe Stripe key for the platform account, baked per-environment
// (dev/staging/prod build separately) alongside the app's other VITE_* client keys.
const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

interface Props {
  organizationId: string;
}

type Status = 'loading' | 'ready' | 'error';

/**
 * Embeds Stripe's Connect "Documents" component so a child care provider can
 * view and download their 1099-K tax form inline.
 *
 * The client secret + publishable key are minted on demand by the
 * `callables-stripe-createAccountSession` callable — we never hold a
 * long-lived secret in the browser, and Connect.js refreshes the session via
 * `fetchClientSecret` when it expires.
 */
export function StripeConnectDocuments({ organizationId }: Props): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [connectInstance, setConnectInstance] = useState<StripeConnectInstance | null>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (!PUBLISHABLE_KEY) {
          logError('VITE_STRIPE_PUBLISHABLE_KEY is not configured for tax forms', undefined, { organizationId });
          setStatus('error');
          return;
        }
        const { clientSecret } = await createAccountSession(organizationId);
        if (cancelled) return;
        if (!clientSecret) {
          logError('Tax forms account session response was missing a client secret', undefined, { organizationId });
          setStatus('error');
          return;
        }

        // Connect.js calls fetchClientSecret immediately on init and again on
        // refresh. Hand back the secret we already minted on the first call,
        // then re-mint for subsequent refreshes.
        let pending: string | null = clientSecret;
        const fetchClientSecret = async (): Promise<string> => {
          if (pending) {
            const secret = pending;
            pending = null;
            return secret;
          }
          try {
            const refreshed = await createAccountSession(organizationId);
            if (!refreshed.clientSecret) {
              throw new Error('Account session refresh returned no client secret');
            }
            return refreshed.clientSecret;
          } catch (error) {
            // Connect.js surfaces its own error UI when this rejects; log so a
            // failed refresh isn't invisible to us.
            logError('Failed to refresh Stripe account session for tax forms', error, { organizationId });
            throw error;
          }
        };

        const instance = loadConnectAndInitialize({ publishableKey: PUBLISHABLE_KEY, fetchClientSecret });
        if (cancelled) return;
        setConnectInstance(instance);
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        logError('Failed to load Stripe Connect account session for tax forms', error, { organizationId });
        setStatus('error');
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (status === 'loading') {
    return <Spinner size={WidgetSizeEnum.LARGE} label={t('common.loading')} />;
  }

  if (status === 'error' || !connectInstance) {
    return (
      <InformationalBanner
        type="error"
        label={t(
          'taxForms.loadError',
          "We couldn't load your tax forms right now. Please refresh the page or try again later."
        )}
      />
    );
  }

  return (
    <ConnectComponentsProvider connectInstance={connectInstance}>
      {/* loadConnectAndInitialize resolves synchronously, so a failure while the
          Stripe SDK loads escapes the init try/catch above. onLoadError is the
          component-level hook that catches it — log it and show our own banner. */}
      <ConnectDocuments
        onLoadError={({ error }) => {
          logError('Stripe ConnectDocuments failed to load for tax forms', error, { organizationId });
          setStatus('error');
        }}
      />
    </ConnectComponentsProvider>
  );
}

export default StripeConnectDocuments;
